import { useEffect, useRef, useState } from "react";
import { X, PanelLeft, Pencil } from "lucide-react";
import { NOTEPAD } from "@/constants/testIds";

export default function TabBar({
  tabs,
  activeId,
  onSelect,
  onClose,
  onRename,
  sidebarCollapsed,
  onExpandSidebar,
}) {
  const [renamingId, setRenamingId] = useState(null);
  const [tempName, setTempName] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (renamingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renamingId]);

  const startRename = (tab) => {
    setRenamingId(tab.id);
    setTempName(tab.title);
  };

  const commitRename = () => {
    if (renamingId && tempName.trim()) {
      onRename(renamingId, tempName.trim());
    }
    setRenamingId(null);
  };

  return (
    <div
      data-testid={NOTEPAD.tabBar}
      className="h-10 bg-[#0A0B10] border-b border-[#2A2F45] flex items-stretch overflow-x-auto"
      style={{ scrollbarWidth: "none" }}
    >
      {sidebarCollapsed && (
        <button
          onClick={onExpandSidebar}
          className="px-3 border-r border-[#2A2F45] text-[#8B949E] hover:text-white transition-colors flex items-center"
          title="Tampilkan sidebar"
        >
          <PanelLeft className="w-4 h-4" />
        </button>
      )}

      {tabs.map((tab) => {
        const active = tab.id === activeId;
        const isRenaming = renamingId === tab.id;
        return (
          <div
            key={tab.id}
            data-testid={NOTEPAD.tab(tab.id)}
            onClick={() => onSelect(tab.id)}
            onDoubleClick={() => startRename(tab)}
            title={isRenaming ? "" : "Double-click untuk ubah nama"}
            className={`group flex items-center gap-2 px-4 min-w-[140px] max-w-[220px] border-r border-[#2A2F45] cursor-pointer transition-colors relative ${
              active
                ? "bg-[#12141D] text-white"
                : "text-[#8B949E] hover:bg-[#12141D]/50 hover:text-white"
            }`}
          >
            {active && (
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-indigo-500" />
            )}

            {isRenaming ? (
              <input
                ref={inputRef}
                data-testid={NOTEPAD.tabRenameInput}
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setRenamingId(null);
                }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0A0B10] border border-indigo-500/50 text-white text-sm px-2 py-0.5 rounded outline-none flex-1 font-mono"
              />
            ) : (
              <span className="text-sm truncate flex-1 font-mono">
                {tab.title || "Tanpa Judul"}
              </span>
            )}

            {!isRenaming && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startRename(tab);
                }}
                data-testid={`tab-rename-${tab.id}`}
                title="Ubah nama"
                className="opacity-0 group-hover:opacity-100 hover:bg-white/10 hover:text-indigo-400 rounded p-0.5 transition-all"
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose(tab.id);
              }}
              data-testid={NOTEPAD.tabCloseBtn(tab.id)}
              title="Tutup tab"
              className="opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded p-0.5 transition-all"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
